import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";

/**
 * Global safety net for page scrolling.
 *
 * The router already resets scroll on push navigations, but late-mounting
 * content, body-scroll locks (mobile drawer, dialogs) and the native iOS
 * WebView can leave a new page sitting at the previous page's position.
 *
 * Rules:
 *  - a new path (forward navigation) always starts at the top
 *  - browser/app BACK and FORWARD keep the restored reading position
 *  - links to a #hash target are left alone so anchors still work
 */
export function NavigationScrollReset() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hash = useRouterState({ select: (s) => s.location.hash });
  // Search changes (e.g. ?open=<exercise>) open new content too.
  const search = useRouterState({ select: (s) => s.location.searchStr });
  const key = `${pathname}?${search}`;
  const lastPath = useRef<string | null>(null);
  const popping = useRef(false);

  useEffect(() => {
    const onPop = () => {
      popping.current = true;
      // Cleared after the router has finished the popstate navigation.
      window.setTimeout(() => {
        popping.current = false;
      }, 400);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (lastPath.current === key) return;
    const isFirstRender = lastPath.current === null;
    lastPath.current = key;
    if (isFirstRender) return;
    if (popping.current) return;
    // Only leave anchors alone when the anchor actually exists on the page.
    if (hash && document.getElementById(decodeURIComponent(hash))) return;

    let cancelled = false;

    const toTop = () => {
      // A Healing Journey return is restoring the member's reading position.
      if ((window as Window & { __rtRestoringScroll?: boolean }).__rtRestoringScroll) return;
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
      } catch {
        window.scrollTo(0, 0);
      }
      // Some scroll containers (native shell, dialogs) scroll instead of the
      // window; reset those too.
      document
        .querySelectorAll<HTMLElement>("[data-scroll-container]")
        .forEach((el) => {
          el.scrollTop = 0;
        });
    };

    // Stop re-asserting as soon as the member scrolls themselves.
    const onUserScroll = () => {
      cancelled = true;
    };
    window.addEventListener("wheel", onUserScroll, { passive: true });
    window.addEventListener("touchmove", onUserScroll, { passive: true });
    window.addEventListener("keydown", onUserScroll);

    toTop();
    // Re-assert across the first ~3s (members-only pages finish their access check late) so content that renders later (lazy
    // sections, images, member-gated panels, slow data) cannot drag the page
    // down after we have already reset it.
    const raf = requestAnimationFrame(() => {
      if (!cancelled) toTop();
    });
    const timers = [40, 120, 250, 450, 700, 1000, 1500, 2200, 3200].map((ms) =>
      window.setTimeout(() => {
        if (!cancelled) toTop();
      }, ms),
    );

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchmove", onUserScroll);
      window.removeEventListener("keydown", onUserScroll);
    };
  }, [key, hash]);

  return null;
}

